/**
 * Funções puras para formatação e parsing de dados de alertas
 */

import allAirportsData from "shared/config/airports/AllAirports/airports.json";
import type { airportPros } from "shared/config/airports/AllAirports/types";
import { getAirports } from "shared/services/contexts/airport.service";
import type {
  AlertChannel,
  AlertOfferMatched,
} from "shared/services/alerts/alerts.types";

/** Ordem canônica dos canais (exibição e payload). */
export const ALERT_CHANNELS: AlertChannel[] = ["email", "push"];

/** Rótulos exibidos para cada canal de notificação. */
export const CHANNEL_LABELS: Record<AlertChannel, string> = {
  email: "E-mail",
  push: "Push",
};

/**
 * Alterna um canal na seleção. Nunca deixa a seleção vazia: desmarcar o único
 * canal ativo é ignorado, pois todo alerta precisa de ao menos um canal.
 */
export function toggleAlertChannel(
  channels: AlertChannel[],
  channel: AlertChannel,
): AlertChannel[] {
  if (!channels.includes(channel)) {
    return ALERT_CHANNELS.filter((c) => c === channel || channels.includes(c));
  }
  if (channels.length === 1) return channels;
  return channels.filter((c) => c !== channel);
}

/**
 * Converte as flags da API (boolean ou 0/1) em lista de canais.
 * Sem nenhuma flag ativa, assume e-mail (padrão do app).
 */
export function channelsFromFlags(flags: {
  sendViaEmail?: boolean | number;
  sendViaPushNotification?: boolean | number;
}): AlertChannel[] {
  const channels: AlertChannel[] = [];
  if (flags.sendViaEmail) channels.push("email");
  if (flags.sendViaPushNotification) channels.push("push");
  return channels.length > 0 ? channels : ["email"];
}

/** Converte a lista de canais nas flags enviadas à API. */
export function channelsToFlags(channels: AlertChannel[]) {
  return {
    sendViaEmail: channels.includes("email"),
    sendViaWhatsapp: false,
    sendViaPushNotification: channels.includes("push"),
  };
}

/**
 * Extrai códigos de aeroportos de um campo formatado como "[GRU,CGH]"
 * Também aceita arrays. Aplica trim, maiúsculas e descarta vazios.
 */
export function parseAirportsField(value: unknown): string[] {
  if (typeof value === "string") {
    const cleaned = value.replace(/[\[\]]/g, "").trim();
    if (!cleaned) return [];
    return cleaned
      .split(",")
      .map((c) => c.trim().toUpperCase())
      .filter((c) => c.length > 0);
  }

  if (Array.isArray(value)) {
    return value
      .map((v) => String(v).trim().toUpperCase())
      .filter((v) => v.length > 0);
  }

  return [];
}

/**
 * Converte um array de códigos IATA para a formatação de corpo da API: "[GRU]"
 */
export function airportsToBody(codes: string[]): string {
  return `[${codes.join(",")}]`;
}

/**
 * Normaliza um alerta bruto da API.
 * Converte booleanos (0/1), ordena datas, e infere matchType se necessário.
 */
export function normalizeAlert(raw: unknown) {
  const toBoolean = (v: unknown) => v === true || v === 1 || v === "1";
  const obj = raw as Record<string, unknown>;

  const departureDate = (
    Array.isArray(obj.departureDate) ? obj.departureDate : []
  )
    .filter((d) => typeof d === "string")
    .sort();

  const returnDate = (Array.isArray(obj.returnDate) ? obj.returnDate : [])
    .filter((d) => typeof d === "string")
    .sort();

  const matchType =
    (obj.matchType as string) ??
    (departureDate.length ? "dateRange" : "onlyPrice");

  return {
    id: String(obj.id ?? ""),
    userID: String(obj.userID ?? ""),
    originAirports: parseAirportsField(obj.originAirports),
    destinationAirports: parseAirportsField(obj.destinationAirports),
    maxPrice: Number(obj.maxPrice) || 0,
    departureDate,
    returnDate,
    isStopped: toBoolean(obj.isStopped),
    sendViaEmail: toBoolean(obj.sendViaEmail),
    sendViaWhatsapp: toBoolean(obj.sendViaWhatsapp),
    sendViaPushNotification: toBoolean(obj.sendViaPushNotification),
    matchType,
    createdAt: String(obj.createdAt ?? ""),
    offerMatched: parseOfferMatched(obj.offerMatched),
  };
}

/**
 * Lê o `offerMatched` de um alerta. Retorna null se não houver oferta ou se
 * faltar o mínimo para exibi-la (id, preço e data de ida).
 */
export function parseOfferMatched(raw: unknown): AlertOfferMatched | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const text = (v: unknown) =>
    typeof v === "string" && v.trim() ? v.trim() : null;
  const price = Number(o.lowestAdultPrice ?? o.price);
  const id = text(o.id);
  const departureDate = text(o.departureDate);

  if (!id || !departureDate || !Number.isFinite(price)) return null;

  return {
    id,
    lowestAdultPrice: price,
    originAirport: text(o.originAirport) ?? "",
    destinationAirport: text(o.destinationAirport) ?? "",
    departureDate,
    returnDate: text(o.returnDate),
    departureAirline: text(o.departureAirline),
    departureAirlineCode: text(o.departureAirlineCode),
    webserviceId: text(o.webserviceId),
    webserviceName: text(o.webserviceName),
    sellerName: text(o.sellerName),
    voopterRedirectUrl: text(o.voopterRedirectUrl),
  };
}

const WEEKDAYS = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
];
const MONTHS = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

/**
 * Formata uma data YYYY-MM-DD como "Segunda, 10 de dezembro" (mesmo formato do
 * app). Retorna "" se a data for inválida.
 */
export function formatDateFullLong(ymd: string): string {
  const match = ymd.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  const [, y, m, d] = match.map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return `${WEEKDAYS[date.getUTCDay()]}, ${d} de ${MONTHS[m - 1]}`;
}
/**
 * Formata um valor em reais via Intl.NumberFormat (BRL).
 * Com `compact`, valores inteiros são exibidos sem centavos ("R$ 1.276").
 */
export function formatBRL(
  value: number,
  options?: { compact?: boolean },
): string {
  const hideCents = options?.compact && Number.isInteger(value);
  const formatter = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    ...(hideCents && { minimumFractionDigits: 0, maximumFractionDigits: 0 }),
  });
  return formatter.format(value);
}

/**
 * Converte centavos para reais (número decimal)
 */
export function centsToBRL(cents: number): number {
  return Math.round(cents) / 100;
}

/** Máximo de dígitos do preço em centavos (99.999,99 → 7 dígitos). */
export const MAX_PRICE_DIGITS = 7;

/**
 * Faz parse de um campo de preço de entrada (texto) em centavos.
 * Remove não dígitos, retorna 0 se vazio e ignora dígitos além do limite
 * de 99.999,99.
 */
export function parsePriceInputToCents(text: string): number {
  const digits = text.replace(/\D/g, "").slice(0, MAX_PRICE_DIGITS);
  return parseInt(digits, 10) || 0;
}

/**
 * Formata um intervalo de datas em formato curto: "DD/MM" ou "DD/MM a DD/MM"
 * Retorna string vazia se o array estiver vazio.
 */
export function formatDateRangeShort(dates: string[]): string {
  if (dates.length === 0) return "";

  const formatDate = (dateStr: string) => {
    const [, _year, month, day] =
      dateStr.match(/(\d{4})-(\d{2})-(\d{2})/) || [];
    return `${day}/${month}`;
  };

  if (dates.length === 1) {
    return formatDate(dates[0]);
  }

  return `${formatDate(dates[0])} a ${formatDate(dates[1])}`;
}

/**
 * Rótulo de um aeroporto ou código de cidade (área metropolitana).
 * `city` e `code` sempre presentes; os demais campos são opcionais:
 * - `airportName`: nome do aeroporto quando o código é de um aeroporto único;
 * - `airports`: nomes dos aeroportos da cidade quando o código é de uma
 *   área metropolitana (ex.: SAO -> ["Congonhas", "Guarulhos", ...]).
 */
export type AirportLabel = {
  city: string;
  code: string;
  airportName?: string;
  airports?: string[];
};

/**
 * Obtém o rótulo de um aeroporto por código IATA.
 * Retorna { city, code } com fallback para o próprio código, além de
 * `airportName` (aeroporto) ou `airports` (código de cidade) quando conhecidos.
 */
export function getAirportLabel(code: string): AirportLabel {
  const airports = getAirports();
  const airport = airports.findAirportByCode(code);
  const label: AirportLabel = {
    city: airport?.city || airport?.name || code,
    code,
  };

  const metropolitanMembers = (allAirportsData as airportPros[])
    .filter(
      (item) =>
        item.metropolitan_area_code === code &&
        item.is_metropolitan_area === 0 &&
        item.airport_code !== code,
    )
    .map((item) => item.airport_name.replace(/\s+Intl$/i, ""))
    .sort((a, b) => a.localeCompare(b, "pt-BR"));

  if (metropolitanMembers.length > 0) {
    label.airports = metropolitanMembers;
  } else if (airport?.city) {
    label.airportName = airport.name;
  }

  return label;
}

/**
 * Rótulo "Aeroporto - Cidade" de um código IATA (ex.: "Guarulhos - São Paulo").
 * Remove o sufixo "Intl" do aeroporto e devolve só a cidade quando o nome do
 * aeroporto é igual a ela (ex.: "Lisboa"). Códigos desconhecidos voltam como estão.
 */
export function formatAirportCityLabel(code: string): string {
  const airport = (allAirportsData as airportPros[]).find(
    (item) => item.airport_code === code,
  );
  if (!airport) return code;

  const airportName = airport.airport_name.replace(/\s+Intl$/i, "");
  return airportName === airport.city_name
    ? airport.city_name
    : `${airportName} - ${airport.city_name}`;
}
